// Brett-Editor wie lichess.org/editor: Figuren setzen, Zugrecht, Rochaderechte, FEN.
import { h, type VNode } from 'snabbdom';
import type { Api } from '@lichess-org/chessground/api';
import type { Key, Piece, Role } from '@lichess-org/chessground/types';
import { Chess } from 'chessops/chess';
import { makeBoardFen, makeFen, parseFen } from 'chessops/fen';
import { board } from '../board';
import { t } from '../i18n';
import { colorName, roleName } from '../roles';

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const ROLES: Role[] = ['king', 'queen', 'rook', 'bishop', 'knight', 'pawn'];
type Tool = { piece: Piece } | 'trash' | 'pointer';

export class EditorCtrl {
  boardFen = START.split(' ')[0];
  turn: 'white' | 'black' = 'white';
  castles = { K: true, Q: true, k: true, q: true };
  tool: Tool = 'pointer';
  flipped = false;
  fenInput = '';
  error = '';
  /** Felder, bei denen die Bilderkennung unsicher war */
  unsure: string[] = [];
  private cg: Api | null = null;

  constructor(readonly redraw: () => void) {}

  open(fen: string | null, unsure: string[] = [], flipped = false) {
    this.unsure = unsure;
    this.flipped = flipped;
    this.load(fen ?? START);
  }

  load(fen: string) {
    const setup = parseFen(fen.trim());
    if (setup.isErr) {
      this.error = t('Diese FEN kann ich nicht lesen.');
      this.redraw();
      return;
    }
    const s = setup.unwrap();
    this.boardFen = makeBoardFen(s.board);
    this.turn = s.turn;
    const cr = makeFen(s).split(' ')[2];
    this.castles = { K: cr.includes('K'), Q: cr.includes('Q'), k: cr.includes('k'), q: cr.includes('q') };
    this.error = '';
    this.cg?.set({ fen: this.boardFen });
    this.redraw();
  }

  fen(): string {
    const cr = (['K', 'Q', 'k', 'q'] as const).filter(k => this.castles[k]).join('') || '-';
    return `${this.boardFen} ${this.turn[0]} ${cr} - 0 1`;
  }

  /** Nur legale Stellungen lassen sich analysieren; Rochaderechte, die nicht passen, werden entfernt. */
  /** Mehr als 16 Figuren einer Farbe kann keine Partie ergeben; Stockfish lehnt so eine Stellung ab. */
  private tooMany(): boolean {
    const setup = parseFen(this.fen());
    return setup.isOk && (setup.unwrap().board.white.size() > 16 || setup.unwrap().board.black.size() > 16);
  }

  validFen(): string | null {
    const setup = parseFen(this.fen());
    if (setup.isErr || this.tooMany()) return null;
    const pos = Chess.fromSetup(setup.unwrap());
    return pos.isOk ? makeFen(pos.unwrap().toSetup()) : null;
  }

  problem(): string {
    const setup = parseFen(this.fen());
    if (setup.isErr) return t('Ungültige Stellung.');
    if (this.tooMany()) return t('Höchstens 16 Figuren je Seite.');
    const pos = Chess.fromSetup(setup.unwrap());
    if (pos.isOk) return '';
    const e = String(pos.error.message ?? pos.error);
    const text: Record<string, string> = {
      ERR_KINGS: 'Jede Seite braucht genau einen König.',
      ERR_PAWNS_ON_BACKRANK: 'Bauern dürfen nicht auf der ersten oder letzten Reihe stehen.',
      ERR_OPPOSITE_CHECK: 'Die Seite, die nicht am Zug ist, steht im Schach.',
      ERR_IMPOSSIBLE_CHECK: 'Dieses Schach kann so nicht entstanden sein.',
      ERR_EMPTY: 'Das Brett ist leer.',
    };
    return text[e] ? t(text[e]) : t('Die Stellung ist nicht spielbar ({e}).', { e });
  }

  /**
   * Wie im lichess-Editor: Ist eine Figur aus der Leiste gewählt, gehört der Klick aufs Brett
   * dem Editor, nicht chessground (das würde sonst gleichzeitig Figuren verschieben).
   */
  setCg(api: Api, el: HTMLElement) {
    this.cg = api;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (this.tool === 'pointer') return;
      e.preventDefault();
      e.stopPropagation();
      const p = 'touches' in e ? e.touches[0] : e;
      if (!p) return;
      const key = api.getKeyAtDomPos([p.clientX, p.clientY]);
      if (key) this.place(key);
    };
    el.addEventListener('mousedown', handler, { capture: true });
    el.addEventListener('touchstart', handler, { capture: true, passive: false });
  }

  private place(key: Key) {
    if (!this.cg || this.tool === 'pointer') return;
    this.unsure = this.unsure.filter(s => s !== key);
    const pieces = new Map<Key, Piece | undefined>();
    pieces.set(key, this.tool === 'trash' ? undefined : this.tool.piece);
    this.cg.setPieces(pieces);
    this.syncFromBoard();
  }

  syncFromBoard() {
    if (!this.cg) return;
    this.boardFen = this.cg.getFen();
    this.redraw();
  }
}

function palette(c: EditorCtrl, color: 'white' | 'black'): VNode {
  return h('div.palette.cg-wrap', [
    ...ROLES.map(role => {
      const active = typeof c.tool === 'object' && c.tool.piece.role === role && c.tool.piece.color === color;
      return h('button', {
        class: { active },
        attrs: { 'aria-label': t('{c} {r} setzen', { c: colorName(color), r: roleName(role) }), 'aria-pressed': String(active) },
        on: { click: () => ((c.tool = { piece: { role, color } }), c.redraw()) },
      }, h(`piece.${role}.${color}`));
    }),
    color === 'white'
      ? h('button.tool', { class: { active: c.tool === 'pointer' }, on: { click: () => ((c.tool = 'pointer'), c.redraw()) } }, t('ziehen'))
      : h('button.tool', { class: { active: c.tool === 'trash' }, on: { click: () => ((c.tool = 'trash'), c.redraw()) } }, t('löschen')),
  ]);
}

export function editorView(c: EditorCtrl): VNode {
  const valid = c.validFen();
  const problem = valid ? '' : c.problem();
  const castle = (k: 'K' | 'Q' | 'k' | 'q', label: string) =>
    h('label.chk', [
      h('input', { attrs: { type: 'checkbox' }, props: { checked: c.castles[k] }, on: { change: (e: Event) => ((c.castles[k] = (e.target as HTMLInputElement).checked), c.redraw()) } }),
      ' ' + label,
    ]);
  return h('section.editor', [
    h('div.editor-board', [
      palette(c, c.flipped ? 'white' : 'black'),
      board('editor-board', {
        fen: c.boardFen,
        orientation: c.flipped ? 'black' : 'white',
        coordinates: true,
        lastMove: undefined,
        movable: { free: true, color: 'both', showDests: false },
        draggable: { enabled: true, deleteOnDropOff: true },
        premovable: { enabled: false },
        highlight: { lastMove: false, custom: new Map(c.unsure.map(s => [s as Key, 'unsure'])) },
        animation: { enabled: false },
        events: { change: () => c.syncFromBoard() },
      }, (api, el) => c.setCg(api, el)),
      palette(c, c.flipped ? 'black' : 'white'),
    ]),
    h('div.side', [
      h('div.fld', [
        h('span.lbl', t('Am Zug')),
        h('div.row', [
          h('label.chk', [h('input', { attrs: { type: 'radio', name: 'turn' }, props: { checked: c.turn === 'white' }, on: { change: () => ((c.turn = 'white'), c.redraw()) } }), ' ' + t('Weiß')]),
          h('label.chk', [h('input', { attrs: { type: 'radio', name: 'turn' }, props: { checked: c.turn === 'black' }, on: { change: () => ((c.turn = 'black'), c.redraw()) } }), ' ' + t('Schwarz')]),
        ]),
      ]),
      h('div.fld', [
        h('span.lbl', t('Rochade')),
        h('div.row', [castle('K', `${t('Weiß')} O-O`), castle('Q', `${t('Weiß')} O-O-O`)]),
        h('div.row', [castle('k', `${t('Schwarz')} O-O`), castle('q', `${t('Schwarz')} O-O-O`)]),
      ]),
      h('div.fld', [
        h('span.lbl', 'FEN'),
        h('input.search.fen', {
          attrs: { 'aria-label': 'FEN', spellcheck: 'false' },
          props: { value: c.fen() },
          on: { change: (e: Event) => c.load((e.target as HTMLInputElement).value) },
        }),
        c.error ? h('p.error', c.error) : null,
      ]),
      h('div.row', [
        h('button.btn', { on: { click: () => c.load(START) } }, t('Grundstellung')),
        h('button.btn', { on: { click: () => c.load('8/8/8/8/8/8/8/8 w - - 0 1') } }, t('Leeren')),
        h('button.btn', { on: { click: () => ((c.flipped = !c.flipped), c.redraw()) } }, t('Drehen')),
      ]),
      c.unsure.length
        ? h('p.warn.small', t('Unsicher erkannt: {s} (schraffiert). Prüfen und bei Bedarf korrigieren.', { s: c.unsure.join(', ') }))
        : null,
      valid
        ? h('a.btn.accent.go', { attrs: { href: `#/analyse/${encodeURIComponent(valid)}${c.flipped ? '?f=1' : ''}` } }, t('Analysieren'))
        : h('p.warn', problem),
    ]),
  ]);
}
